import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { addDays, weekDates } from '../../core/calendar';
import { standardLookup } from '../../core/course';
import { courseStandardIds, fitsUnit, itemHasActions, standardsOn, type StandardSpan, type UnitSpans } from '../../core/days';
import type { AnalyticsEvent, PlannerAction } from '../../core/events';
import { collabMaterialHref } from '../../core/attachments';
import { brushUpLink, lessonCheckLink } from '../../core/handoff';
import { weekRows, type PlanbookDay, type ScheduledPlan, type WeekRow } from '../../core/planbook';
import type { DayItem, IsoDate, PlanRecord } from '../../core/types';
import { call, collabAccess, itemPath, itemsPath, toApiError, type DayItemInput, type DayItemResponse, type DayItemsOrderResponse, type GetToken, type ItemsRangeResponse } from '../api';
import { linkTo, planHref } from '../navigation';
import { unitColor } from '../unit-color';
import { ItemForm } from './ItemForm';
import { day, type EditRequest } from './UnitRow';

/** One request at a time per plan: answers null, sending nothing, while that plan has one in flight. */
export type RunForPlan = <T>(planId: string, request: () => Promise<T>) => Promise<T | null>;
export type Track = (action: PlannerAction, properties?: AnalyticsEvent['properties']) => void;
/** A built plan as My week shows it: its record, schedule, and standards' spans, short label, and colour by creation order. */
export interface WeekPlan extends ScheduledPlan { record: PlanRecord; label: string; colorIndex: number }
/** One plan with its row for a week. */
export interface PlanRow { plan: WeekPlan; row: WeekRow }
type ItemsByPlan = Record<string, DayItem[]>;
/** Each plan's items by date, each day's in position order. */
export type ItemsByDay = Map<string, Map<IsoDate, DayItem[]>>;

/** The school days of the shown week across every plan, in order. */
const schoolDaysOf = (rows: readonly PlanRow[], monday: IsoDate): IsoDate[] =>
  weekDates(monday).filter(d => rows.some(({ row }) => row.days.some(x => x.date === d && x.kind === 'school')));
/** Today when it is a school day of the shown week, else that week's first school day, else none (KTD5, KTD6). */
const defaultDay = (school: readonly IsoDate[], today: IsoDate): IsoDate | null => (school.includes(today) ? today : school[0] ?? null);
const weekday = (date: IsoDate, style: 'long' | 'short') => new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', { weekday: style, timeZone: 'UTC' });
/** "Thursday, Oct 22": how a day is named. */
const dayName = (date: IsoDate) => `${weekday(date, 'long')}, ${day(date)}`;

/** Each plan with its row for the week of `monday`. */
export const planRows = (plans: readonly WeekPlan[], monday: IsoDate): PlanRow[] =>
  weekRows(plans, monday).map((row, i) => ({ plan: plans[i]!, row }));

/**
 * A range's items by plan and day from `GET /api/items` (KTD1), fetched when the range is shown;
 * null until it answers. `update` applies a finished write here at once, then refetches the range
 * so the view matches the server. Each write bumps a generation: a fetch started before it is
 * stale, and its answer, which may predate the write, is dropped.
 */
export function useRangeItems(getToken: GetToken, from: IsoDate, to: IsoDate) {
  const key = `${from}:${to}`;
  const [result, setResult] = useState<{ key: string; items: ItemsByPlan } | { key: string; error: string } | null>(null);
  const [generation, setGeneration] = useState(0);
  const latest = useRef(0);
  useEffect(() => {
    let live = true; const controller = new AbortController();
    const range = `${from}:${to}`; const started = latest.current;
    const fresh = () => live && started === latest.current;
    void call<ItemsRangeResponse>(getToken, `/api/items?from=${from}&to=${to}`, { signal: controller.signal })
      .then(response => { if (fresh()) setResult({ key: range, items: response.items }); })
      // A failed refetch keeps the items already shown, which include the write.
      .catch(failure => { if (fresh()) setResult(r => (r?.key === range && 'items' in r ? r : { key: range, error: toApiError(failure).message })); });
    return () => { live = false; controller.abort(); };
  }, [getToken, from, to, generation]);
  const update = useCallback((planId: string, change: (items: DayItem[]) => DayItem[]) => {
    latest.current += 1;
    setGeneration(latest.current);
    setResult(r => (r && r.key === key && 'items' in r ? { key, items: { ...r.items, [planId]: change(r.items[planId] ?? []) } } : r));
  }, [key]);
  const current = result?.key === key ? result : null;
  const items = useMemo(() => (current && 'items' in current ? byDay(current.items) : null), [current]);
  return { items, error: current && 'error' in current ? current.error : null, update };
}

function byDay(items: ItemsByPlan): ItemsByDay {
  return new Map(Object.entries(items).map(([planId, list]) => {
    const dates = new Map<IsoDate, DayItem[]>();
    for (const item of list) {
      const onDay = dates.get(item.date);
      if (onDay) onDay.push(item); else dates.set(item.date, [item]);
    }
    for (const onDay of dates.values()) onDay.sort((a, b) => a.position - b.position);
    return [planId, dates];
  }));
}

const NO_ITEMS: readonly DayItem[] = [];
/** A plan's items on a date, in their order. */
export const itemsOn = (items: ItemsByDay | null, planId: string, date: IsoDate): readonly DayItem[] => items?.get(planId)?.get(date) ?? NO_ITEMS;

/**
 * One planbook week (R1, R2): a row per plan, labelled with its unit and the codes taught that
 * week, and a box per shown weekday holding only the teacher's items. A break is greyed and
 * named; a weekday outside the year is blank. Month shows it read-only; Week fills the boxes.
 */
export function WeekGrid({ monday, dates, today, rows, label, cell }: {
  monday: IsoDate; dates: readonly IsoDate[]; today: IsoDate; rows: readonly PlanRow[];
  label(entry: PlanRow): ReactNode; cell(entry: PlanRow, day: PlanbookDay): ReactNode;
}) {
  return <div className="planbook-grid ph-no-capture" data-ph-no-capture role="table" aria-label={`Week of ${day(monday)}`} style={{ '--days': dates.length } as CSSProperties}>
    <div role="row" className="planbook-grid-row">
      <div role="columnheader" className="planbook-corner"><span className="sr-only">Subject</span></div>
      {dates.map(d => <div key={d} role="columnheader" className={`planbook-day-head${d === today ? ' today' : ''}`}>
        {weekday(d, 'short')} {Number(d.slice(8))}{d === today && ' · Today'}
      </div>)}
    </div>
    {rows.map(entry => {
      const { plan, row } = entry;
      return <div key={row.planId} role="row" className="planbook-grid-row" style={{ '--unit-color': unitColor(plan.colorIndex) } as CSSProperties}>
        <div role="rowheader" className="planbook-label">{label(entry)}</div>
        {row.days.filter(d => dates.includes(d.date)).map(d => <div key={d.date} role="cell" aria-label={`${row.label}, ${dayName(d.date)}`}
          className={`planbook-cell ${d.kind === 'outside_year' ? 'outside' : d.kind}${d.date === today ? ' today' : ''}`}>
          {d.kind === 'break' && <span className="planbook-break">{d.breakName}</span>}
          {d.newUnit && <span className="planbook-new">New: {d.newUnit.name}</span>}
          {cell(entry, d)}
        </div>)}
      </div>;
    })}
  </div>;
}

/** "Math 2 · Odd and even": a row's label and its unit that week. */
export const rowTitle = (row: WeekRow) => (row.unit ? `${row.label} · ${row.unit.name}` : row.label);

type Code = WeekRow['codes'][number];
/** A row's label: its title and the codes taught that week, each plain unless `code` draws it; Week adds span controls below. */
export function RowLabel({ row, code, children }: { row: WeekRow; code?(c: Code): ReactNode; children?: ReactNode }) {
  return <>
    <b className="planbook-label-title"><span className="cal-swatch" aria-hidden="true" />{rowTitle(row)}</b>
    {row.codes.length > 0 && <span className="planbook-codes">{row.codes.map(c => code?.(c) ?? <span key={c.id} className="planbook-code">{c.code}</span>)}</span>}
    {children}
  </>;
}

type Form = { planId: string; date: IsoDate; item?: DayItem } | null;

/**
 * The Week level (R6, KTD5): one planbook week full size, editable. Each box adds, edits,
 * deletes, and reorders the teacher's items; a tagged item is one tap from "Brush up" and
 * "Check my lesson". Each code on a row label opens its span's controls, and each label links to
 * its plan's editor, as the planbook is where a plan opens. On a phone it shows
 * one day at a time with a day switcher (KTD6). Every write waits for that plan's last request.
 */
export function WeekLevel({ monday, today, plans, narrow, busy, getToken, run, collabAttachments = false, onMoveSpan, onTrack }: {
  monday: IsoDate; today: IsoDate; plans: readonly WeekPlan[]; narrow: boolean; busy: ReadonlySet<string>;
  getToken: GetToken; run: RunForPlan; collabAttachments?: boolean; onMoveSpan(planId: string, edit: EditRequest): Promise<unknown>; onTrack: Track;
}) {
  const rows = useMemo(() => planRows(plans, monday), [plans, monday]);
  const { items, error: loadError, update } = useRangeItems(getToken, monday, addDays(monday, 4));
  const [chosen, setChosen] = useState<{ monday: IsoDate; date: IsoDate } | null>(null);
  const [form, setForm] = useState<Form>(null);
  const [error, setError] = useState<string | null>(null);
  const [openSpan, setOpenSpan] = useState<string | null>(null);
  // An open form or span belongs to the week it was opened in.
  const [shownMonday, setShownMonday] = useState(monday);
  if (shownMonday !== monday) { setShownMonday(monday); setForm(null); setOpenSpan(null); }
  // Items change only once the week's items are in: a write before then would not show.
  const ready = items !== null && loadError === null;
  const school = schoolDaysOf(rows, monday);
  const selected = chosen?.monday === monday ? chosen.date : defaultDay(school, today) ?? monday;
  const dates = narrow ? [selected] : weekDates(monday);

  /** Runs a request for a plan; answers whether it finished, showing `failed` if it did not. */
  const send = async <T,>(planId: string, failed: string, request: () => Promise<T>, done: (answer: T) => void): Promise<boolean> => {
    setError(null);
    try {
      const answer = await run(planId, request);
      if (answer === null) return false;
      done(answer);
      return true;
    } catch (failure) {
      setError(`${failed} ${toApiError(failure).message}`);
      return false;
    }
  };
  const save = (planId: string, date: IsoDate, editing: DayItem | undefined) => (fields: DayItemInput) => {
    const { method, path, body } = editing
      ? { method: 'PUT', path: itemPath(planId, editing.id), body: fields }
      : { method: 'POST', path: itemsPath(planId), body: { date, ...fields } };
    return send(planId, 'We could not save that item.', () => call<DayItemResponse>(getToken, path, { method, body: JSON.stringify(body) }),
      // The same item twice, from a refetch that already has it, shows once.
      ({ item }) => { update(planId, list => (list.some(i => i.id === item.id) ? list.map(i => (i.id === item.id ? item : i)) : [...list, item])); setForm(null); });
  };
  const remove = (planId: string, item: DayItem) => send(planId, 'We could not delete that item.', () => call(getToken, itemPath(planId, item.id), { method: 'DELETE' }),
    () => update(planId, list => list.filter(i => i.id !== item.id)));
  const move = (planId: string, date: IsoDate, index: number, by: -1 | 1) => {
    const ids = itemsOn(items, planId, date).map(i => i.id);
    [ids[index], ids[index + by]] = [ids[index + by]!, ids[index]!];
    return send(planId, 'We could not move that item.', () => call<DayItemsOrderResponse>(getToken, `${itemsPath(planId)}/order`, { method: 'PUT', body: JSON.stringify({ date, ids }) }),
      ({ items: ordered }) => update(planId, list => [...list.filter(i => i.date !== date), ...ordered]));
  };
  const open = (next: Form) => { setError(null); setForm(next); };

  const label = ({ plan, row }: PlanRow) => <RowLabel row={row} code={c => {
    const found = spanOf(plan.spans, c.id, monday);
    if (!found) return null;
    const key = `${plan.record.id}:${c.id}`;
    return <button key={c.id} type="button" className={`planbook-code${found.span.moved ? ' moved' : ''}`} aria-expanded={openSpan === key}
      aria-label={`${c.code}, ${whenIn(found.span, monday)}${found.span.moved ? ', moved' : ''}. Move or resize`}
      title={standardLookup(plan.record.standards)(c.id).statement} onClick={() => setOpenSpan(o => (o === key ? null : key))}>{c.code}</button>;
  }}>
    <a className="planbook-edit-plan" {...linkTo(planHref(plan.record.id))}>Edit plan<span className="sr-only"> for {row.label}</span></a>
    {row.codes.map(c => {
      const found = openSpan === `${plan.record.id}:${c.id}` ? spanOf(plan.spans, c.id, monday) : null;
      return found && <SpanControls key={c.id} code={c.code} span={found.span} unitId={found.unitId} days={found.days} disabled={busy.has(plan.record.id)}
        onEdit={edit => void onMoveSpan(plan.record.id, edit)} />;
    })}
  </RowLabel>;

  const cell = ({ plan, row }: PlanRow, d: PlanbookDay) => {
    const planId = plan.record.id;
    const list = itemsOn(items, planId, d.date);
    const planBusy = busy.has(planId) || !ready;
    const textOf = standardLookup(plan.record.standards);
    return <>
      {list.length > 0 && <ul className="planbook-items" aria-label={`${row.label} items`}>
        {list.map((item, i) => <li key={item.id} className="planbook-item">
          <span className="planbook-item-title">{item.title}</span>
          {item.link && <a className="planbook-item-link" href={item.link} target="_blank" rel="noopener noreferrer">Open link<span className="sr-only"> for {item.title}</span></a>}
          {item.materials.length > 0 && <ul className="planbook-materials" aria-label={`${item.title} attachments`}>
            {item.materials.map(m => <li key={m.materialId}>
              <a className="planbook-material" href={collabMaterialHref(m)} target="_blank" rel="noopener noreferrer">{m.title}</a>
              {itemHasActions(item) && <a className="planbook-handoff" target="_blank" rel="noopener noreferrer"
                href={lessonCheckLink(plan.record.course, item.standardIds.map(textOf), item.link, { group: m.groupId, material: m.materialId })}
                onClick={() => onTrack('lesson_check_clicked', { plan_id: planId, standard_count: item.standardIds.length, with_material: true })}>Check<span className="sr-only"> {m.title}</span></a>}
            </li>)}
          </ul>}
          {itemHasActions(item) && <span className="planbook-item-actions">
            <a className="planbook-handoff" href={brushUpLink(plan.record.course, item.standardIds.map(textOf))} target="_blank" rel="noopener noreferrer"
              onClick={() => onTrack('brush_up_clicked', { plan_id: planId, standard_count: item.standardIds.length })}>Brush up</a>
            {item.materials.length === 0 && <a className="planbook-handoff" href={lessonCheckLink(plan.record.course, item.standardIds.map(textOf), item.link)} target="_blank" rel="noopener noreferrer"
              onClick={() => onTrack('lesson_check_clicked', { plan_id: planId, standard_count: item.standardIds.length })}>Check my lesson</a>}
          </span>}
          <span className="planbook-item-tools">
            <button type="button" className="text-button" aria-label={`Move ${item.title} up`} disabled={planBusy || i === 0} onClick={() => void move(planId, d.date, i, -1)}>↑</button>
            <button type="button" className="text-button" aria-label={`Move ${item.title} down`} disabled={planBusy || i === list.length - 1} onClick={() => void move(planId, d.date, i, 1)}>↓</button>
            <button type="button" className="text-button" aria-label={`Edit ${item.title}`} disabled={planBusy} onClick={() => open({ planId, date: d.date, item })}>Edit</button>
            <button type="button" className="text-button" aria-label={`Delete ${item.title}`} disabled={planBusy} onClick={() => void remove(planId, item)}>Delete</button>
          </span>
        </li>)}
      </ul>}
      {d.kind === 'school' && <button type="button" className="planbook-add" aria-label={`Add to ${row.label}, ${dayName(d.date)}`} disabled={!ready} onClick={() => open({ planId, date: d.date })}>+ Add</button>}
    </>;
  };

  const formPlan = form && plans.find(p => p.record.id === form.planId);
  const formPlanId = form?.planId ?? null;
  // One `CollabAccess` per open form's plan, so the picker's search keeps its identity across renders.
  const collab = useMemo(() => (collabAttachments && formPlanId ? collabAccess(getToken, formPlanId) : undefined), [collabAttachments, formPlanId, getToken]);
  return <>
    <h2 className="planbook-title">Week of {day(monday)}</h2>
    {narrow && <div className="day-switcher" role="group" aria-label="Choose a day">
      {weekDates(monday).map(d => <button key={d} type="button" className={`planbook-switch-day${d === today ? ' today' : ''}`} aria-pressed={d === selected}
        aria-current={d === today ? 'date' : undefined} disabled={!school.includes(d)} onClick={() => setChosen({ monday, date: d })}>
        <span className="planbook-switch-weekday">{weekday(d, 'short')}</span><span>{Number(d.slice(8))}</span>
      </button>)}
    </div>}
    {(loadError || error) && <p className="error-message" role="alert">{loadError ? `We could not load this week's items. ${loadError}` : error}</p>}
    <p className="sr-only" role="status">{busy.size > 0 ? 'Saving…' : ''}</p>
    <div className="planbook-big"><WeekGrid monday={monday} dates={dates} today={today} rows={rows} label={label} cell={cell} /></div>
    {form && formPlan && <ItemForm key={`${form.planId}:${form.date}:${form.item?.id ?? 'new'}`}
      heading={form.item ? `Edit ${form.item.title}` : `Add to ${formPlan.label}, ${dayName(form.date)}`}
      initial={form.item} course={courseStandardIds(formPlan.record)} covering={standardsOn(formPlan.spans, form.date)}
      textOf={standardLookup(formPlan.record.standards)} busy={busy.has(form.planId)} submit={form.item ? 'Save item' : 'Add item'} collab={collab}
      onSubmit={save(form.planId, form.date, form.item)} onCancel={() => setForm(null)} />}
    {!narrow && <p className="muted planbook-hint">{collabAttachments ? 'Tagged items get Brush up and Check my lesson, or a Check on each attachment.' : 'Tagged items get Brush up and Check my lesson.'} Choose a standard code to move its days.</p>}
  </>;
}

/** The span of a standard that covers a day of the shown week, with its unit and that unit's day count. */
function spanOf(units: readonly UnitSpans[], standardId: string, monday: IsoDate) {
  const dates = weekDates(monday);
  for (const unit of units) {
    const span = unit.spans.find(s => s.standardId === standardId && s.dates.some(d => dates.includes(d)));
    if (span) return { span, unitId: unit.unitId, days: unit.days.length };
  }
  return null;
}

/** "Monday to Friday": the shown week's days a span covers. */
function whenIn(span: StandardSpan, monday: IsoDate) {
  const days = weekDates(monday).filter(d => span.dates.includes(d));
  const first = weekday(days[0]!, 'long'); const last = weekday(days.at(-1)!, 'long');
  return first === last ? first : `${first} to ${last}`;
}

/** Earlier, later, longer, shorter, each only while the span stays inside its unit, and reset (R6). */
function SpanControls({ code, span, unitId, days, disabled, onEdit }: {
  code: string; span: StandardSpan; unitId: string; days: number; disabled: boolean; onEdit(edit: EditRequest): void;
}) {
  const move = (start: number, length: number) => ({ type: 'move_span' as const, standardId: span.standardId, unitId, start, length });
  const options = [
    { text: 'Earlier', label: `Move ${code} a day earlier`, edit: move(span.start - 1, span.length) },
    { text: 'Later', label: `Move ${code} a day later`, edit: move(span.start + 1, span.length) },
    { text: 'Longer', label: `Make ${code} a day longer`, edit: move(span.start, span.length + 1) },
    { text: 'Shorter', label: `Make ${code} a day shorter`, edit: move(span.start, span.length - 1) },
  ];
  return <div className="planbook-span-controls" role="group" aria-label={`Move ${code}`}>
    {options.map(o => <button key={o.text} type="button" className="text-button" aria-label={o.label} disabled={disabled || !fitsUnit(o.edit, days)} onClick={() => onEdit(o.edit)}>{o.text}</button>)}
    {span.moved && <button type="button" className="text-button" aria-label={`Put ${code} back on the spread`} disabled={disabled} onClick={() => onEdit({ type: 'reset_span', standardId: span.standardId })}>Reset</button>}
  </div>;
}
